// src/pages/CreditPlan.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";

function CreditPlan() {
  const navigate = useNavigate();

  // 1. USER AUTH CHECK
  const authDataString = localStorage.getItem("authData");
  const parsedAuthData = authDataString ? JSON.parse(authDataString) : null;
  const userId = parsedAuthData?.user?.sub || null;
  //console.log("[CreditPlan] User ID:", userId);

  // 2. ONBOARDING BANNER
  const [showOnboardingBanner, setShowOnboardingBanner] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("hideOnboardingBanner")) {
      setShowOnboardingBanner(true);
    }
  }, []);

  const handleDismissBanner = () => {
    setShowOnboardingBanner(false);
  };

  const handleHideBannerForever = () => {
    localStorage.setItem("hideOnboardingBanner", "true");
    setShowOnboardingBanner(false);
  };

  // 3. STATE: credits, selection, error, loading
  const [credits, setCredits] = useState(null);
  const [selectedOption, setSelectedOption] = useState("");
  const [autoRenew, setAutoRenew] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // 4. FETCH USER CREDITS
  useEffect(() => {
    if (!userId) {
      setError("You are not logged in.");
      //console.log("[CreditPlan] User not logged in.");
      return;
    }
    async function fetchCredits() {
      //console.log("[CreditPlan] Fetching credits...");
      setIsLoading(true);
      setError("");
      try {
        const API_URL = process.env.REACT_APP_SETTINGS_URL + '?userId=' + userId;

        
        const response = await fetch(API_URL, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch settings.");
        }
        const data = await response.json();
        //console.log("[CreditPlan] Raw settings response:", data);
        const parsedBody = JSON.parse(data.body);
        setCredits(parsedBody.creditsRemaining);
        //console.log("[CreditPlan] Credits fetched:", parsedBody.creditsRemaining);
      } catch (err) {
        setError(err.message || "Failed to fetch credits.");
        //console.error("[CreditPlan] Error fetching credits:", err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchCredits();
  }, [userId]);

  // 5. PLAN MAPPING
  const planMapping = {
    "Monthly-300-28": {
      id: "credit_plan_300",
      quantity: 1,
      description: "300 Credits Package",
      amount: 28,
      numberOfCredits: 300,
    },
    "PAYG-300-30": {
      id: "credit_plan_300",
      quantity: 1,
      description: "300 Credits Package",
      amount: 30,
      numberOfCredits: 300,
    },
    "Monthly-800-55": {
      id: "credit_plan_800",
      quantity: 1,
      description: "800 Credits Package",
      amount: 55,
      numberOfCredits: 800,
    },
    "PAYG-800-60": {
      id: "credit_plan_800",
      quantity: 1,
      description: "800 Credits Package",
      amount: 60,
      numberOfCredits: 800,
    },
    "Monthly-1400-75": {
      id: "credit_plan_1400",
      quantity: 1,
      description: "1400 Credits Package",
      amount: 75,
      numberOfCredits: 1400,
    },
    "PAYG-1400-90": {
      id: "credit_plan_1400",
      quantity: 1,
      description: "1400 Credits Package",
      amount: 90,
      numberOfCredits: 1400,
    },
    "Monthly-3000-140": {
      id: "credit_plan_3000",
      quantity: 1,
      description: "3000 Credits Package",
      amount: 140,
      numberOfCredits: 3000,
    },
    "PAYG-3000-150": {
      id: "credit_plan_3000",
      quantity: 1,
      description: "3000 Credits Package",
      amount: 150,
      numberOfCredits: 3000,
    },
  };

  // Define tiers for dynamic rendering.
  const tiers = [
    {
      tier: "300 Credits",
      monthly: planMapping["Monthly-300-28"],
      payg: planMapping["PAYG-300-30"],
    },
    {
      tier: "800 Credits",
      monthly: planMapping["Monthly-800-55"],
      payg: planMapping["PAYG-800-60"],
    },
    {
      tier: "1400 Credits",
      monthly: planMapping["Monthly-1400-75"],
      payg: planMapping["PAYG-1400-90"],
    },
    {
      tier: "3000 Credits",
      monthly: planMapping["Monthly-3000-140"],
      payg: planMapping["PAYG-3000-150"],
    },
  ];

  // 6. SELECTION HANDLERS
  const handleRadioChange = (value) => {
    //console.log("[CreditPlan] Selected option:", value);
    setSelectedOption(value);
    if (!value.startsWith("PAYG")) {
      setAutoRenew(false);
    }
  };

  const handleAutoRenewChange = (paygValue, checked) => {
    //console.log("[CreditPlan] Auto-renew toggle for", paygValue, ":", checked);
    if (selectedOption === paygValue) {
      setAutoRenew(checked);
    } else {
      setAutoRenew(false);
    }
  };

  const handleSelectMonthly = () => {
    //console.log("[CreditPlan] Monthly option selected, resetting autoRenew.");
    setAutoRenew(false);
  };

  // 7. PURCHASE SUBMIT
  const handleSubmit = async (e) => {
    e.preventDefault();
    //console.log("[CreditPlan] Form submitted.");

    if (!userId) {
      setError("You are not logged in. Please log in.");
      return;
    }
    if (!selectedOption) {
      setError("Please select a plan before purchasing.");
      return;
    }

    const planDetails = planMapping[selectedOption];
    if (!planDetails) {
      setError("Selected plan is not recognized.");
      return;
    }

    //console.log("[CreditPlan] Plan details for order creation:", planDetails);
    setIsLoading(true);

    try {
      // STEP 1: Create the PaymentIntent first.
      const paymentPayload = {
        items: [
          {
            id: planDetails.id,
            description: planDetails.description,
            amount: planDetails.amount,
            quantity: planDetails.quantity,
          },
        ],
      };

      const intentRes = await fetch(process.env.REACT_APP_PGINT_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(paymentPayload),
        }
      );
      if (!intentRes.ok) {
        throw new Error("Failed to create PaymentIntent.");
      }
      const intentData = await intentRes.json();
      //console.log("[CreditPlan] Raw PaymentIntent response:", intentData);

      // Parse the PaymentIntent object from the lambda response.
      const paymentIntent = JSON.parse(intentData.body);
      const clientSecret = paymentIntent.client_secret;
      //console.log("[CreditPlan] Extracted clientSecret:", clientSecret);

      // STEP 2: Build the order payload including the PaymentIntent details.
      const orderPayload = {
        userId,
        autoRenew,
        option: selectedOption,
        numberOfCredits: planDetails.numberOfCredits,
        amount: planDetails.amount,
        // Store paymentIntent as a string (using its id if available)
        paymentIntent: paymentIntent.id || clientSecret,
      };

      // Create the order in DynamoDB.
      const orderRes = await fetch(process.env.REACT_APP_ORDER_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(orderPayload),
        }
      );
      if (!orderRes.ok) {
        throw new Error("Failed to create order in DynamoDB.");
      }
      const orderData = await orderRes.json();
      // Parse the order number from the lambda response.
      const parsedOrder = JSON.parse(orderData.body);
      const { orderNumber } = parsedOrder;
      //console.log("[CreditPlan] Created order in DynamoDB with order number:", orderNumber);

      // STEP 3: Navigate to Checkout, passing clientSecret, orderNumber, userId, and purchase details.
      navigate("/checkout", {
        state: {
          clientSecret,
          orderNumber,
          userId,
          purchaseDetails: {
            id: planDetails.id,
            description: planDetails.description,
            amount: planDetails.amount,
            quantity: planDetails.quantity,
            autoRenew,
            option: selectedOption,
          },
        },
      });
    } catch (err) {
      setError(err.message || "An error occurred during purchase.");
      //console.error("[CreditPlan] Error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // 8. AUTH CHECK
  if (!userId) {
    return (
      <Layout>
        <section className="py-12 bg-white sm:py-16 lg:py-20">
          <div className="px-4 mx-auto max-w-7xl">
            <h2 className="text-2xl font-bold text-red-500">You are not logged in.</h2>
          </div>
        </section>
      </Layout>
    );
  }

  // 9. RENDER
  return (
    <Layout>
      {/* Fancy Onboarding Banner */}
      {showOnboardingBanner && (
        <div className="relative isolate flex items-center gap-x-6 overflow-hidden bg-gray-50 px-6 py-2.5 sm:px-3.5 sm:before:flex-1">
          <div
            className="absolute top-1/2 left-[max(-7rem,calc(50%-52rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-pink-300 to-indigo-300 opacity-30"
              style={{
                clipPath:
                  "polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)",
              }}
            />
          </div>
          <div
            className="absolute top-1/2 left-[max(45rem,calc(50%+8rem))] -z-10 -translate-y-1/2 transform-gpu blur-2xl"
            aria-hidden="true"
          >
            <div
              className="aspect-[577/310] w-[36.0625rem] bg-gradient-to-r from-pink-300 to-indigo-300 opacity-30"
              style={{
                clipPath:
                  "polygon(74.8% 41.9%, 97.2% 73.2%, 100% 34.9%, 92.5% 0.4%, 87.5% 0%, 75% 28.6%, 58.5% 54.6%, 50.1% 56.8%, 46.9% 44%, 48.3% 17.4%, 24.7% 53.9%, 0% 27.9%, 11.9% 74.2%, 24.9% 54.1%, 68.6% 100%, 74.8% 41.9%)",
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <p className="text-sm leading-6 text-gray-900">
              <strong className="font-semibold">Welcome to PreciseDraft!</strong>
              <svg viewBox="0 0 2 2" className="mx-2 inline h-1 w-1 fill-current" aria-hidden="true">
                <circle cx="1" cy="1" r="1" />
              </svg>
              Check out our{" "}
              <a href="/onboarding" className="text-blue-600 underline font-semibold">
                Onboarding Page
              </a>{" "}
              for guidance.
            </p>
            <button
              type="button"
              onClick={handleHideBannerForever}
              className="flex-none rounded-full bg-gray-900 px-3.5 py-1 text-sm font-semibold text-white shadow-sm hover:bg-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
            >
              Don’t Show Again
            </button>
          </div>
          <div className="flex flex-1 justify-end">
            <button type="button" className="-m-3 p-3 focus-visible:outline-offset-[-4px]" onClick={handleDismissBanner}>
              <span className="sr-only">Dismiss</span>
              <svg className="h-5 w-5 text-gray-900" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72-3.72a.75.75 0 1 0 1.06-1.06L10 8.94 6.28 5.22Z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Main container for the credit plan purchase flow */}
      <div className="container mx-auto mt-10 max-w-4xl bg-white shadow-md p-10 rounded-md">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Purchase Additional Credits</h2>
        <p className="text-gray-600 mb-2">
          Your current balance:{" "}
          <span className="font-semibold text-blue-600">
            {credits === null ? "Loading..." : credits + " credits"}
          </span>
        </p>
        <p className="text-gray-600 mb-8">
          Select one plan below to buy more credits. For PAYG, you can opt into auto‑renew when credits fall below 10.
        </p>

        {error && <p className="text-red-500 mb-4 font-semibold">{error}</p>}

        <form onSubmit={handleSubmit}>
          {/* ===== DESKTOP TABLE VIEW ===== */}
          <div className="mt-6 sm:block">
            <table className="w-full max-w-4xl mx-auto lg:table-fixed font-pj">
              <thead>
                <tr className="divide-x divide-gray-200 bg-gray-800 text-white">
                  <th className="py-8 pr-4 text-left">Credit Tier</th>
                  <th className="px-4 py-8 text-center">Monthly</th>
                  <th className="px-4 py-8 text-center">Pay As You Go</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tierData, index) => (
                  <tr key={index} className="divide-x divide-gray-200">
                    <td className="py-4 pr-4 border-b border-gray-200 font-medium">{tierData.tier}</td>
                    <td className="px-4 py-4 border-b border-gray-200 text-center bg-blue-50">
                      <div className="flex flex-col items-center space-y-2" style={{ height: "4rem" }}>
                        <span className="block text-xl font-bold text-blue-800">${tierData.monthly.amount}</span>
                        <label className="inline-flex items-center">
                          <input
                            type="radio"
                            name="selectedOption"
                            value={`Monthly-${tierData.monthly.numberOfCredits}-${tierData.monthly.amount}`}
                            className="form-radio text-blue-800"
                            checked={selectedOption === `Monthly-${tierData.monthly.numberOfCredits}-${tierData.monthly.amount}`}
                            onChange={(e) => {
                              handleRadioChange(e.target.value);
                              handleSelectMonthly();
                            }}
                          />
                          <span className="ml-2 text-blue-800">Select</span>
                        </label>
                      </div>
                    </td>
                    <td className="px-4 py-4 border-b border-gray-200 text-center bg-green-50">
                      <div className="flex flex-col items-center">
                        <div className="flex flex-col items-center space-y-2" style={{ height: "4rem" }}>
                          <span className="block text-xl font-bold text-green-800">${tierData.payg.amount}</span>
                          <label className="inline-flex items-center">
                            <input
                              type="radio"
                              name="selectedOption"
                              value={`PAYG-${tierData.payg.numberOfCredits}-${tierData.payg.amount}`}
                              className="form-radio text-green-800"
                              checked={selectedOption === `PAYG-${tierData.payg.numberOfCredits}-${tierData.payg.amount}`}
                              onChange={(e) => handleRadioChange(e.target.value)}
                            />
                            <span className="ml-2 text-green-800">Select</span>
                          </label>
                        </div>
                        <div className="flex items-center justify-center bg-green-100 px-3 py-2">
                          <label className="inline-flex items-center">
                            <input
                              type="checkbox"
                              name={`autoRenew-${tierData.payg.numberOfCredits}`}
                              className="form-checkbox h-4 w-4 text-green-800"
                              checked={selectedOption === `PAYG-${tierData.payg.numberOfCredits}-${tierData.payg.amount}` && autoRenew}
                              onChange={(e) =>
                                handleAutoRenewChange(`PAYG-${tierData.payg.numberOfCredits}-${tierData.payg.amount}`, e.target.checked)
                              }
                            />
                            <span className="ml-2 text-sm text-green-800">Auto‑renew when credits &lt; 10</span>
                          </label>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
                {/* Contact us rows */}
                <tr className="divide-x divide-gray-200">
                  <td className="py-4 pr-4 border-b border-gray-200 font-medium">5000 Credits</td>
                  <td className="px-4 py-4 border-b border-gray-200 text-center bg-gray-100" colSpan={2}>
                    Contact us
                  </td>
                </tr>
                <tr className="divide-x divide-gray-200">
                  <td className="py-4 pr-4 border-b border-gray-200 font-medium">10000 Credits</td>
                  <td className="px-4 py-4 border-b border-gray-200 text-center bg-gray-100" colSpan={2}>
                    Contact us
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="mt-4 text-center text-sm text-gray-600">
              Note: Credits rollover for monthly plans and do not expire for either plan.
            </p>
          </div>
          {/* ===== END DESKTOP VIEW ===== */}

          <div className="mt-8 text-center">
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center justify-center px-6 py-3 text-base font-bold text-white transition-all duration-200 bg-gray-900 border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 hover:bg-gray-700"
            >
              {isLoading ? "Processing..." : "Purchase Credits"}
            </button>
          </div>
        </form>

        {isLoading && (
          <p className="mt-4 text-blue-500 font-semibold">Processing your purchase...</p>
        )}
        {error && <p className="mt-4 text-red-500 font-semibold">{error}</p>}
      </div>
    </Layout>
  );
}

export default CreditPlan;
